




























































































































import { computed, defineComponent, inject, useContext, onMounted, ref } from '@nuxtjs/composition-api';
import { Product as ProductJsonLdSchema, WithContext } from 'schema-dts';
import { CombinedError } from 'villus';
import { useProductContext } from '~/features/productContext';
import { useSeoMeta, useJsonLd } from '~/features/seo';
import { purify } from '~/directives/purify-html';
import { CONFIG_STORE_CURRENCY } from '~/utils/provides';
import { toNonNullable } from '~/utils/collections';
import { useEventBus } from '~/features/events';
import { useAlerts } from '~/features/alerts';
import { useI18n } from '~/features/i18n';
import { useAddProductToCartContext } from '~/features/productAddToCart';
import { useSlideShow } from '~/features/product/slideShow';
import { useAuth } from '~/features/auth';
import { useVisitedProduct } from '~/features/product';
import { TRACKING_EVENTS } from '~/features/trackingHandlers';
import { useStoreConfig } from '~/features/storeConfig';
import { MageWorxGiftCardTypeEnum } from '~/graphql-types.gen';

export default defineComponent({
  directives: {
    purify,
  },
  setup() {
    const currencyCode = inject(CONFIG_STORE_CURRENCY);
    const { isLoggedIn } = useAuth();
    const { limited_qty_threshold } = useStoreConfig();

    const { route, $config, app } = useContext();
    const { toggle: toggleSlideShow } = useSlideShow();

    const { error } = useAlerts();
    const { t } = useI18n();
    const { emit } = useEventBus();
    const { product } = useProductContext({
      init: true,
    });
    const { onVisitProduct } = useVisitedProduct();
    const { addToCart: addItem, quantity } = useAddProductToCartContext();

    const digitalGiftCardProduct = computed(
      () => product.value.type === 'MageWorxGiftCards' && product.value.giftCardType === MageWorxGiftCardTypeEnum.Email
    );

    const digitalCardData = ref();
    const isDigitalCardDataValid = computed(() => {
      if (!digitalCardData.value) {
        return false;
      }
      return digitalCardData.value.giftCardToEmail && digitalCardData.value.giftCardFromName;
    });

    onMounted(() => {
      // Fire ViewContent event on product page view
      /* app.$fireFbEvent('ViewContent', {
        content_name: product.value?.name || '',
        content_category: product.value?.mainCategory,
        content_ids: [product.value?.id],
        content_type: 'product',
        value: Number(product.value?.price).toFixed(2),
        currency: 'EGP',
      }); */

      // Fire GTM event for product details view
      app.$gtag('product_detail_views', { product: product.value });

      if (isLoggedIn.value && product.value.sku) {
        onVisitProduct(product.value.id || 0);
      }
    });

    useSeoMeta(
      computed(() => ({
        title: product.value?.meta_title || product.value?.name,
        description: product.value?.meta_description,
        keywords: product.value?.meta_keywords,
        image: product.value?.image?.url,
      }))
    );

    const isLimitedStock = computed(() => {
      return product.value?.stock && limited_qty_threshold.value >= product.value?.stock;
    });

    const isGroupedProduct = computed(() => {
      return !!product.value?.groupItems?.length;
    });

    onMounted(() => {
      product.value && emit(TRACKING_EVENTS.PRODUCT_VIEW, product.value);
    });

    async function addToCart() {
      try {
        await addItem();
        emit('ITEM_ADDED', product.value);
      } catch (err) {
        if (/the requested qty is not available/i.test((err as CombinedError).message)) {
          error(t('qtyUnavailable', [product.value?.name]) as string);
          return;
        }

        if (
          /Product that you are trying to add is not available/i.test((err as CombinedError).message) ||
          /Some of the products are out of stock/i.test((err as CombinedError).message)
        ) {
          error(t('outOfStockError', [product.value?.name]) as string);
        }

        if (/requiredConfigurationOption/i.test((err as CombinedError).message)) {
          error(t('error').toString(), t('requiredConfigurationOption').toString());
        }
        // eslint-disable-next-line no-console
        console.log(err);
      }
    }

    /**
     * This is google's structured data for rich results feature
     * It let's google show the product in a rich preview panel if they want
     * https://developers.google.com/search/docs/data-types/product
     */
    useJsonLd(
      computed(() => {
        const url = $config.appURL;
        const data: WithContext<ProductJsonLdSchema> = {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: product.value?.name as string,
          sku: product.value?.sku as string,
          image: toNonNullable(product.value?.media_gallery).map(img => img.url as string),
          description: product.value?.meta_description as string,
          brand: product.value?.brand
            ? {
                '@type': 'Brand',
                name: product.value.brand.name,
              }
            : undefined,
        };

        data.offers = {
          '@type': 'Offer',
          url: `${url}${route.value.fullPath}`,
          priceCurrency: currencyCode?.value || 'EGP',
          price: product.value?.price,
          itemCondition: 'https://schema.org/NewCondition',
          availability: product.value?.stock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        };

        if (product.value?.review_count) {
          data.aggregateRating = {
            '@type': 'AggregateRating',
            ratingValue: Number((product.value.rating_summary * 5) / 100).toFixed(1),
            reviewCount: product.value.review_count,
          };
        }

        return data;
      })
    );

    return {
      product,
      isLimitedStock,
      quantity,
      addToCart,
      isGroupedProduct,
      toggleSlideShow,
      MageWorxGiftCardTypeEnum,
      digitalGiftCardProduct,
      digitalCardData,
      isDigitalCardDataValid,
    };
  },
  head: {},
});
